<template>
  <!-- 껍데기는 SectionCard 하나로 통일한다. 예전에는 이 카드만 .content-card 에
       background:#fff 와 box-shadow 를 하드코딩하고 있어서, 옆에 나란히 놓이는
       '다가오는 마감'(토큰 사용)과 그림자 농도·모서리 반경이 미묘하게 달랐다. -->
  <SectionCard
    title="미배정 업무"
    :count="count > 99 ? 99 : count"
    :tone="count > 0 ? 'warn' : 'default'"
    :to="{ name: 'Tasks', query: { scope: 'unassigned' } }"
    action-label="전체"
  >
    <div v-if="tasks.length" class="un">
      <div v-for="t in tasks" :key="t.id" class="un__row">
        <button type="button" class="un__main" @click="open(t)">
          <span class="un__title u-trunc">{{ t.title }}</span>
          <span class="un__meta">
            <span class="tone-chip">{{ t.department ? t.department.name : '전사' }}</span>
            <!-- 우선순위는 '매우중요/중요' 만 색을 준다.
                 '일반' 까지 초록으로 칠하면 목록이 신호등이 되는데, 이 앱에서
                 초록은 완료를 뜻한다 — 미배정 업무는 하나도 완료가 아니다. -->
            <span class="tone-chip" :class="priorityTone(t.priority)">{{ priorityLabel(t.priority) }}</span>
          </span>
        </button>

        <!-- 되돌리기 어려운 배정이라 스와이프가 아니라 명시적 버튼이다 -->
        <el-button
          v-if="canClaim(t)"
          class="un__claim"
          size="small"
          type="primary"
          plain
          round
          :loading="claiming === t.id"
          @click="claim(t)"
        >가져가기</el-button>
      </div>
    </div>

    <div v-else class="empty-state">
      <div class="empty-state__icon">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3.5 13.5h4l1.5 3h6l1.5-3h4" /><path d="M5.5 5.5h13l2 8v5h-17v-5z" />
        </svg>
      </div>
      <div class="empty-state__title">미배정 업무가 없습니다</div>
      <div class="empty-state__hint">담당자 없이 등록된 업무가 생기면 여기 모입니다</div>
    </div>
  </SectionCard>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import SectionCard from '@/components/common/SectionCard.vue'
import { getPriorityLabel } from '@/utils/taskStatus'

export default {
  name: 'UnassignedTasksCard',
  components: { SectionCard },
  setup() {
    const store = useStore()
    const router = useRouter()
    const tasks = ref([])
    const claiming = ref(null)
    const count = computed(() => store.getters.unassignedTaskCount)

    function open(t) { router.push({ name: 'TaskDetail', params: { id: t.id } }) }

    /** el-tag 의 tagType 대신 primitives 의 tone-chip 수식어로 매핑한다 */
    function priorityTone(p) {
      if (p === 'high') return 'tone-chip--danger'
      if (p === 'medium') return 'tone-chip--warning'
      return ''
    }

    const canClaim = (t) => store.getters.canClaimTask(t)

    async function load() {
      try {
        tasks.value = await store.dispatch('queryTasks', { scope: 'unassigned', status: ['pending', 'in_progress'], limit: 6 })
        store.dispatch('fetchTaskBadges').catch(() => {})
      } catch (e) { /* noop */ }
    }

    async function claim(t) {
      claiming.value = t.id
      try {
        await store.dispatch('claimTask', t.id)
        ElMessage.success('내 업무로 가져왔습니다.')
        await load()
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '가져오기에 실패했습니다.')
      } finally {
        claiming.value = null
      }
    }

    onMounted(load)
    return { tasks, claiming, count, open, claim, canClaim, priorityLabel: getPriorityLabel, priorityTone }
  }
}
</script>

<style scoped>
.un { display: flex; flex-direction: column; }

.un__row {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  border-bottom: 1px solid var(--color-border);
}
.un__row:last-child { border-bottom: 0; }

/* 행 전체가 아니라 왼쪽 덩어리만 상세로 간다 — 오른쪽 '가져가기' 와
   같은 버튼 안에 들어 있으면 중첩 버튼이 되고, 탭이 어느 쪽으로 갈지 모호해진다. */
.un__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--sp-1);
  padding: var(--sp-2) 0;
  min-height: var(--tap-min);
  border: 0;
  background: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}
.un__main:active { background: var(--color-surface-2); }

.un__title {
  font-size: var(--fs-md);
  font-weight: var(--fw-medium);
  color: var(--color-text);
}

.un__meta { display: flex; align-items: center; gap: var(--sp-2); min-width: 0; }

/* el-button 의 small 은 24px 이라 손가락으로 누르기엔 얇다. 라벨 크기는 두고
   높이만 데스크톱 28 / 모바일 44(--tap-min)로 올린다.
   .un__claim 은 el-button 의 루트에 얹히는 클래스라 여기서 직접 잡을 수 있다
   (Vue 3 는 자식 컴포넌트 루트에 부모의 scope 속성을 함께 붙인다). */
.un__claim { flex-shrink: 0; min-height: 28px; }

/* 태블릿도 터치다(.only-* 유틸과 무관하게 이 버튼은 항상 보인다) */
@media (max-width: 1023px) { /* --bp-tablet-max */
  .un__claim { min-height: var(--tap-min); padding: 0 var(--sp-4); }
}

@media (max-width: 767px) { /* --bp-mobile-max */
  .un__main { min-height: var(--tap-comfortable); }
}
</style>
